import styled from "styled-components";
import { fontFamilies, fontSizes, fontWeights } from "components/shared/tokens/typography";
import { zSpacing } from "components/shared/tokens/spacing";
import red from "components/shared/tokens/color/red";
import grey from "components/shared/tokens/color/grey";
import box from "components/shared/tokens/box";

const DARK_NAVY = "#293142";

export const PageContainer = styled.div`
  width: 100%;
  height: 100vh;
  background: #ffffff;
  font-family: ${fontFamilies.regular};
  display: flex;
  justify-content: center;
`;

export const ContentArea = styled.div`
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  padding: ${zSpacing.z4} ${zSpacing.z3} ${zSpacing.z5} ${zSpacing.z3};
  height: 100%;
  ${({ $gap }) => $gap && `gap: ${$gap};`}
`;

export const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  font-size: ${fontSizes.z400};
  font-weight: 600;
  color: ${DARK_NAVY};
  cursor: pointer;
  font-family: ${fontFamilies.semiBold};
  ${({ $mb }) => `margin-bottom: ${$mb ?? zSpacing.z5};`}

  span {
    margin-left: ${zSpacing.z3};
  }
`;

export const BottomActionWrapper = styled.div`
  margin-top: auto;
  width: 100%;
  ${({ $pt }) => $pt && `padding-top: ${$pt};`}
`;

export const PrimaryButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: ${zSpacing.z3};
  font-size: ${fontSizes.z300};
  font-family: ${fontFamilies.semiBold};
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease-in-out;
  background: ${({ disabled }) => (disabled ? grey.z200 : red.z500)};
  color: #ffffff;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};

  &:active:not(:disabled) {
    opacity: 0.85;
  }
`;

export const QuestionTitle = styled.h1`
  font-size: 2.2rem;
  font-family: ${fontFamilies.semiBold};
  color: ${DARK_NAVY};
  margin-bottom: 28px;
  line-height: 1.3;
`;

export const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${zSpacing.z3};
  flex-grow: 1;
`;

export const OptionCard = styled.div`
  border: 1px solid ${({ $isSelected }) => ($isSelected ? red.z500 : grey.z100)};
  border-radius: 12px;
  padding: 18px 20px;
  font-size: ${fontSizes.z300};
  color: ${({ $isSelected }) => ($isSelected ? DARK_NAVY : grey.z600)};
  font-family: ${fontFamilies.regular};
  cursor: pointer;
  background: ${({ $isSelected }) => ($isSelected ? `${red.z50}` : "#ffffff")};
  transition: all 0.2s ease-in-out;
  box-shadow: ${({ $isSelected }) => ($isSelected ? `0 0 0 1px ${red.z500}` : "none")};

  &:active {
    background: ${grey.z95};
  }
`;

export const OtherInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  font-size: ${fontSizes.z300};
  color: ${DARK_NAVY};
  font-family: ${fontFamilies.regular};
  background: transparent;

  &::placeholder {
    color: ${grey.z500};
  }
`;

export const NoteTitle = styled.h2`
  font-size: ${fontSizes.z500};
  font-family: ${fontFamilies.semiBold};
  color: ${DARK_NAVY};
  margin-bottom: 28px;
  text-transform: uppercase;
`;

export const NoteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${zSpacing.z4};
  flex-grow: 1;
`;

export const NoteItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${zSpacing.z3};
`;

export const NoteIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid ${grey.z100};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  img {
    width: 22px;
    height: 22px;
    object-fit: contain;
  }
`;

export const NoteText = styled.div`
  font-size: ${fontSizes.z200};
  color: ${grey.z600};
  line-height: 1.4;
  font-family: ${fontFamilies.regular};
`;

export const OtpTitle = styled.h1`
  font-size: ${fontSizes.z600};
  color: ${DARK_NAVY};
  font-family: ${fontFamilies.semiBold};
  line-height: 1.2;
  margin: 0 0 ${zSpacing.z2} 0;
`;

export const OtpSubTitle = styled.div`
  font-size: ${fontSizes.z200};
  color: ${grey.z600};
  line-height: 1.4;
  font-family: ${fontFamilies.regular};
`;

export const OtpWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: ${zSpacing.z2};
`;

export const otpInputStyles = {
  width: "48px",
  height: "54px",
  borderRadius: "12px",
  border: `1px solid ${grey.z300}`,
  boxShadow: "none",
  fontSize: "24px",
  fontWeight: 700,
  color: DARK_NAVY,
  textAlign: "center",
  background: "#ffffff",
  marginRight: `${zSpacing.z2}`,
};

export const ErrorText = styled.div`
  color: ${red.z500};
  font-size: ${fontSizes.z200};
  font-family: ${fontFamilies.regular};
`;

export const CharCount = styled.div`
  color: ${grey.z600};
  font-size: ${fontSizes.z100};
  font-family: ${fontFamilies.regular};
  margin-left: auto;
`;

export const TimerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${zSpacing.z2};
`;

export const TimerText = styled.div`
  font-size: ${fontSizes.z400};
  color: ${DARK_NAVY};
  font-family: ${fontFamilies.semiBold};
`;

export const ResendWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${zSpacing.z1};
`;

export const ResendNoteText = styled.div`
  font-size: ${fontSizes.z200};
  color: ${grey.z600};
  font-family: ${fontFamilies.regular};
`;

export const ResendButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ disabled }) => (disabled ? `${red.z300}` : red.z500)};
  font-size: ${fontSizes.z200};
  padding: 0;
  font-family: ${fontFamilies.semiBold};
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
`;

export const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: -8vh;
`;

export const IllustrationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: ${zSpacing.z6};

  img {
    width: 220px;
    height: auto;
    object-fit: contain;
  }
`;

export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${zSpacing.z3};
  text-align: center;
`;

export const DeletedTitle = styled.h1`
  font-size: 2.2rem;
  color: ${DARK_NAVY};
  font-family: ${fontFamilies.semiBold};
  margin: 0;
  line-height: 1.3;
`;

export const DeletedSubTitle = styled.p`
  font-size: ${fontSizes.z200};
  color: ${grey.z600};
  font-family: ${fontFamilies.regular};
  margin: 0;
  line-height: 1.5;
  max-width: 320px;
`;

export const SplashContainer = styled.div`
  position: fixed;
  inset: 0;
  background: ${red.z500};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

export const SplashLogoText = styled.h1`
  font-size: 4.2rem;
  color: #ffffff;
  font-family: ${fontFamilies.semiBold};
  margin: 0 0 ${zSpacing.z2} 0;
  line-height: 1;
`;

export const SplashSubText = styled.p`
  font-size: ${fontSizes.z200};
  color: #ffffff;
  font-family: ${fontFamilies.semiBold};
  margin: 0;
  letter-spacing: 0.4em;
  margin-left: 0.4em;
`;
